import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { PromoAssignmentParams } from '../types/promotion.types';
import {
  toPromoAssignmentFilterSearch,
  toPromoAssignmentFilterValues,
} from '../utils/promotionFilters';

const route = getRouteApi('/_auth/promotion/distribution');

const normalizeFilterValues = (values: Record<string, string>) =>
  toPromoAssignmentFilterValues(toPromoAssignmentFilterSearch(values));

export function usePromoAssignmentFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const appliedValues = useMemo(() => toPromoAssignmentFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    shouldWaitForApply: true,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toPromoAssignmentFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const nameSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  const params = useMemo<PromoAssignmentParams>(
    () => ({
      search: search.search,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
      userId: search.userId,
      promoItemId: search.promoItemId,
      ...(search.activeState === 'all' ? {} : { isActive: search.activeState === 'active' }),
    }),
    [search, defaultPeriod],
  );

  return { ...filters, ...nameSearch, search, params };
}
